<template>
    <div class="card-ctn" :class="{disabled: isDisabled}">
        <div @click="cardClicked()" :class="classes">
            <h3 class="card--title">{{ refLabel }}</h3>
            <span v-if="isRevolutMethod" class="card--subtitle">{{ revolutSubtitle }}</span>
        </div>
    </div>
    <!-- Modale plein écran TPE Revolut -->
    <RevolutPaymentModal
        v-if="showRevolutModal"
        @paid="onRevolutPaid"
        @close="onRevolutClose"
    />
</template>
<script setup lang="ts">
    import { useVfm } from "vue-final-modal"
    import { inject, ref, toRef, watch } from "vue"
    import { useCartStore } from "@/stores/CartStore"
    import { computed } from "vue"
    import { IPaymentTransaction } from "@/interfaces/PaymentTransaction"
    import { AppPrice } from "@/tools/Price"
    import { toast } from "vue3-toastify"
    import { useTpeStore } from "@/stores/TpeStore"
    import { useAppStore } from "@/stores/AppStore"
    import { useRouter } from "vue-router"
    import { useEventStore } from "@/stores/EventStore"
    import { PaymentMethodEnum } from "@/interfaces/Order"
    import emitter from '@/tools/EventBus/EventBus'
    import { usePaymentTerminalStore } from "@/stores/PaymentTerminalStore"
    import RevolutPaymentModal from "@/components/molecules/modal/RevolutPaymentModal.vue"

    const cartStore = useCartStore()
    const tpeStore = useTpeStore()
    const appStore = useAppStore()
    const terminalStore = usePaymentTerminalStore()
    const amountField = ref<number>(null)
    const leftToPay = ref<AppPrice>(null)
    const amountToReturn = ref<AppPrice>(null)
    const onError = ref<boolean>(false)
    const showRevolutModal = ref<boolean>(false)
    const emit = defineEmits(['submitted', 'cancelled'])
    const router = useRouter()
    const eventStore = useEventStore()

    const props = defineProps({
        type: {type: String, required: true},
        subType: {type: String, required: false, default: null},
        isDefault: {type: Boolean, required: false, default: true},
        label: {type: String, required: false, default: ''}
    })

    const refLabel = ref(props.label)
    if (props.isDefault === true) {
        refLabel.value = PaymentMethodEnum[props.type] || props.type
    }

    const isRevolutMethod = computed(() => props.type === 'creditCardTerminalRevolut')

    const revolutSubtitle = computed(() => {
        if (!isRevolutMethod.value) return ''
        const def = terminalStore.defaultTerminal
        return def ? def.label : 'Sélection au moment du paiement'
    })

    const isDisabled = computed(() => {
        if (isStripeTerminalMethod.value) {
            return (tpeStore.reader == null)
        }
        if (isRevolutMethod.value) {
            return !terminalStore.hasTerminals
        }
        return false
    })

    const isStripeTerminalMethod = computed(() => {
        return (props.type == 'creditCardTerminal')
    })

    const cardClicked = async () => {
        if (!isDisabled.value) {
            if (isRevolutMethod.value) {
                // Flux Revolut : on enregistre le mode de paiement avant
                // d'ouvrir la modale, pour que SummaryView puisse detecter
                // le flow asynchrone (polling jusqu'a Order.completed).
                cartStore.paymentMethod = {
                    method: props.type,
                    customMethod: {
                        value: props.subType,
                        label: props.label
                    }
                }
                showRevolutModal.value = true
                return
            }
            const price = cartStore.totalCart.amount
            if (price > 0) {
                leftToPay.value = new AppPrice(price)
            }
            else {
                leftToPay.value = new AppPrice(0)
            }
            amountField.value = null
            await submitPayment()
        }
        else {
            if (isStripeTerminalMethod.value) {
                toast('Vous devez sélectionner votre terminal de paiement dans le menu "Réglages"')
            }
            if (isRevolutMethod.value) {
                toast('Aucun terminal Revolut n\'est attribué à votre organisation')
            }
            onError.value = true
            setTimeout(() => onError.value = false , 1000);
        }
    }

    const onRevolutPaid = async () => {
        showRevolutModal.value = false
        await cartStore.nextStep()
    }

    // Fermeture de la modale Revolut SANS paiement effectif
    // (annulation, refus, timeout, fermeture manuelle). On réinitialise
    // `cartStore.paymentMethod` : sinon le bouton "Continuer" du panier
    // reste actif et un clic enverrait un `/add-payment` Revolut classique
    // (sans transaction Revolut sous-jacente). Cas reproduit en prod :
    // commande complétée avec OrderPayment Revolut sans `remoteId`,
    // alors qu'aucun encaissement réel n'a eu lieu.
    const onRevolutClose = () => {
        showRevolutModal.value = false
        cartStore.paymentMethod = null
    }

    const classes = computed(() => {
        const classes = ['card', 'card__payment']

        if (cartStore.paymentMethod && cartStore.paymentMethod.method == props.type && cartStore.paymentMethod.customMethod?.value == props.subType) {
            classes.push('active')
        }
        if (isDisabled.value) {
            classes.push('disabled')
        }
        if (onError.value) {
            classes.push('on-error')
        }
        return classes
    })

    const submitPayment = async () => {

        if (cartStore.isOrderExpired) {
            toast.error('La commande a expirée')
            await cartStore.cancel()
            router.push({name: 'ticket', params: { eventId: eventStore.currentEvent?._id }})
            return false
        }

        cartStore.paymentMethod = {
            method: props.type,
            customMethod: {
                value: props.subType,
                label: props.label
            }
        }

        emit('submitted', cartStore.paymentMethod);
    }

</script>